import { MoreVertical, Copy, Trash2, ExternalLink } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

const PLATFORM_COLORS = {
    meta: { bg: 'rgba(24, 119, 242, 0.1)', color: '#1877F2', label: 'Meta' },
    google: { bg: 'rgba(234, 67, 53, 0.1)', color: '#EA4335', label: 'Google' },
    tiktok: { bg: 'rgba(0, 0, 0, 0.1)', color: '#000000', label: 'TikTok' },
    linkedin: { bg: 'rgba(10, 102, 194, 0.1)', color: '#0A66C2', label: 'LinkedIn' }
};

const STATUS_STYLES = {
    draft: { bg: 'var(--warning-100)', color: 'var(--warning-700)' },
    pending: { bg: 'var(--primary-100)', color: 'var(--primary-700)' }
};

export default function FunnelCard({ funnel, onLoad, onDuplicate, onDelete }) {
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const platform = PLATFORM_COLORS[funnel.platform] || PLATFORM_COLORS.meta;
    const status = STATUS_STYLES[funnel.status] || STATUS_STYLES.draft;

    // Count node types
    const nodes = funnel.nodes || [];
    const campaignCount = nodes.filter(n => n.type === 'campaign').length;
    const adSetCount = nodes.filter(n => n.type === 'adset').length;
    const adCount = nodes.filter(n => n.type === 'ad').length;

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Unknown date';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diffInMs = now - date;
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInDays === 0) {
            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
            if (diffInHours === 0) {
                const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
                return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
            }
            return `${diffInHours}h ago`;
        } else if (diffInDays === 1) {
            return 'Yesterday';
        } else if (diffInDays < 7) {
            return `${diffInDays}d ago`;
        } else {
            return date.toLocaleDateString();
        }
    };

    const handleCardClick = () => {
        onLoad(funnel.id);
    };

    const handleMenuClick = (e) => {
        e.stopPropagation();
        setShowMenu(!showMenu);
    };

    const handleDuplicate = (e) => {
        e.stopPropagation();
        setShowMenu(false);
        onDuplicate(funnel.id);
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        setShowMenu(false);
        onDelete(funnel.id);
    };

    return (
        <div
            onClick={handleCardClick}
            style={{
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: 'var(--color-surface)',
                position: 'relative'
            }}
            className="funnel-card"
        >
            {/* Header with platform and menu */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 'var(--space-3)'
            }}>
                {/* Platform badge */}
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    padding: '2px 8px',
                    backgroundColor: platform.bg,
                    color: platform.color,
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase'
                }}>
                    {platform.label}
                </span>

                {/* Menu button */}
                <div ref={menuRef} style={{ position: 'relative' }}>
                    <button
                        onClick={handleMenuClick}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 'var(--space-1)',
                            color: 'var(--color-text-tertiary)',
                            borderRadius: 'var(--radius-sm)'
                        }}
                    >
                        <MoreVertical size={16} />
                    </button>
                    {showMenu && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: 'var(--space-1)',
                            backgroundColor: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 100,
                            minWidth: '140px',
                            overflow: 'hidden'
                        }}>
                            <button
                                onClick={(e) => { e.stopPropagation(); handleCardClick(); setShowMenu(false); }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    width: '100%',
                                    padding: 'var(--space-2) var(--space-3)',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    color: 'var(--color-text-primary)',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    textAlign: 'left'
                                }}
                            >
                                <ExternalLink size={14} />
                                Open
                            </button>
                            <button
                                onClick={handleDuplicate}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    width: '100%',
                                    padding: 'var(--space-2) var(--space-3)',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    color: 'var(--color-text-primary)',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    textAlign: 'left'
                                }}
                            >
                                <Copy size={14} />
                                Duplicate
                            </button>
                            <button
                                onClick={handleDelete}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    width: '100%',
                                    padding: 'var(--space-2) var(--space-3)',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    color: 'var(--color-error)',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    textAlign: 'left'
                                }}
                            >
                                <Trash2 size={14} />
                                Delete
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Funnel name */}
            <h3 style={{
                margin: '0 0 var(--space-2) 0',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            }}>
                {funnel.name || 'Untitled Funnel'}
            </h3>

            {/* Status badge */}
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 6px',
                backgroundColor: status.bg,
                color: status.color,
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.65rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                marginBottom: 'var(--space-3)'
            }}>
                {funnel.status || 'draft'}
            </span>

            {/* Node counts */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-3)',
                fontSize: '0.8rem',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--space-3)'
            }}>
                <span>{campaignCount} campaign{campaignCount !== 1 ? 's' : ''}</span>
                <span>{adSetCount} ad set{adSetCount !== 1 ? 's' : ''}</span>
                <span>{adCount} ad{adCount !== 1 ? 's' : ''}</span>
            </div>

            {/* Last updated */}
            <div style={{
                fontSize: '0.75rem',
                color: 'var(--color-text-tertiary)'
            }}>
                Updated {formatDate(funnel.updatedAt)}
            </div>

            <style>{`
                .funnel-card:hover {
                    border-color: var(--color-primary);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                    transform: translateY(-2px);
                }
            `}</style>
        </div>
    );
}
